/**
 * i18n de la LANDING pública (src/screens/Home.jsx) — rediseño "El Cierre".
 * Se mergea en translations.js igual que comandas.js / costs.js. Claves planas
 * con puntos, prefijo `landing.` (las viejas `home.*` quedaron sin uso).
 *
 * Convención: `**texto**` dentro de una cadena se renderiza en negrita
 * (helper <Rich> en Home.jsx). No hay HTML en las traducciones.
 */
const landing = {
  es: {
    // ── nav de secciones ──
    "landing.nav.noche": "La noche",
    "landing.nav.sistema": "El sistema",
    "landing.nav.cuenta": "La cuenta",
    "landing.nav.precios": "Precios",
    "landing.nav.cta": "Hablemos",
    "landing.nav.aria": "Secciones de la página",

    // ── hero ──
    "landing.hero.kicker": "Sistema de gestión para restaurantes",
    "landing.hero.t1": "Al final de",
    "landing.hero.t2": "la noche, todo",
    "landing.hero.t3": "se reduce a",
    "landing.hero.t4": "un número.",
    "landing.hero.sub": "Y hoy ese número sale de un cuaderno, de la memoria del encargado y de una calculadora que nadie revisa. **RestaurAdmin te acompaña de la carta a la caja** para que salga solo — y sea el correcto.",
    "landing.hero.ctaPrimary": "Quiero empezar",
    "landing.hero.ctaSecondary": "Hacé la cuenta vos mismo",
    "landing.hero.m1": "restaurantes",
    "landing.hero.m2": "provincias",
    "landing.hero.m3": "en Google",
    "landing.hero.m4": "desde {precio}/mes",

    // ── ticket del hero ──
    "landing.tk.close": "Cierre de caja · sáb 23:52",
    "landing.tk.mesas": "Mesas cobradas",
    "landing.tk.cubiertos": "Cubiertos",
    "landing.tk.ticket": "Ticket promedio",
    "landing.tk.efectivo": "Efectivo",
    "landing.tk.debito": "Débito",
    "landing.tk.credito": "Crédito",
    "landing.tk.transferencia": "Transferencia",
    "landing.tk.total": "Total",
    "landing.tk.dif": "Diferencia de caja",
    "landing.tk.cuadra": "La caja cuadra",
    "landing.tk.propina": "Propina a repartir",
    "landing.tk.apps": "Te deben las apps",
    "landing.tk.legal1": "Reporte de control interno",
    "landing.tk.legal2": "No válido como factura",
    "landing.tk.cap": "El cierre del sábado,\ncomo lo ve el dueño.",

    // ── band de números ──
    "landing.band.l1": "Restaurantes activos",
    "landing.band.l2": "Provincias, más Uruguay",
    "landing.band.l3": "Estrellas en Google",
    "landing.band.l4": "Planillas de Excel en paralelo",

    // ── 01 · el problema ──
    "landing.s1.kicker": "El problema",
    "landing.s1.h2": "No es que no sepas cuánto vendiste. Es que lo sabés tarde, y a medias.",
    "landing.s1.lead": "Los datos de tu local existen. El tema es que están repartidos en cinco lugares que no se hablan entre sí, y el que los tiene que juntar al final del mes sos vos.",
    "landing.s1.aHead": "Cómo lo hacés hoy",
    "landing.s1.aTitle": "Cinco lugares\nque no se hablan.",
    "landing.s1.a1": "La comanda se escribe a mano y alguien la transcribe cuando puede.",
    "landing.s1.a2": "Los costos viven en una planilla que actualizaste por última vez en marzo.",
    "landing.s1.a3": "El precio nuevo se lo mandás por WhatsApp al que te hizo la carta, y esperás.",
    "landing.s1.a4": "El arqueo se hace de memoria: si falta plata, te enterás la semana que viene.",
    "landing.s1.a5": "Sacaste un plato de la cocina y la carta impresa lo sigue ofreciendo.",
    "landing.s1.aFoot": "Cinco lugares. Ningún número confiable.",
    "landing.s1.bHead": "Cómo queda",
    "landing.s1.bTitle": "Un solo lugar,\ny el número sale solo.",
    "landing.s1.b1": "El pedido se carga una vez, en la mesa, y sale impreso en la cocina.",
    "landing.s1.b2": "El costo de cada plato se calcula solo con la receta que cargaste una vez.",
    "landing.s1.b3": "El precio lo cambiás vos desde el celular y ya está en el QR de la mesa.",
    "landing.s1.b4": "La caja compara medio por medio y te dice si cuadra antes de que te vayas.",
    "landing.s1.b5": "Sacás un plato y desaparece de la carta, del PDF y del POS al mismo tiempo.",
    "landing.s1.bFoot": "Un solo lugar. Un solo número.",

    // ── 02 · la noche ──
    "landing.s2.kicker": "La noche, renglón por renglón",
    "landing.s2.h2": "Todo esto va a pasar igual. La diferencia es quién lo anota.",
    "landing.s2.lead": "No son funciones sueltas en una grilla de tarjetas: es tu sábado, hora por hora, y qué hace el sistema en cada renglón.",
    "landing.s2.who1": "Vos",
    "landing.s2.who2": "El mozo",
    "landing.s2.who3": "La caja",
    "landing.s2.r1t": "Subís el precio de la milanesa desde el celular.",
    "landing.s2.r1p": "Tocás el precio en la lista, apretás **Subir menú** y el que escanea el QR dos minutos después ya ve el nuevo. Sin imprenta, sin reimprimir el QR, sin depender de nadie.",
    "landing.s2.r1m": "Carta digital",
    "landing.s2.r1n": "Se acabó el pescado: tocás el ojo y desaparece de la carta sin perder la foto, el precio ni la receta.",
    "landing.s2.r2t": "Entra una reserva para cuatro y te avisa el teléfono.",
    "landing.s2.r2p": "Llega como notificación al celular **aunque tengas la app cerrada**. Confirmás con un toque, el sistema le asigna la mesa más chica donde entra ese grupo y se te abre el WhatsApp de confirmación ya escrito, con el nombre, la hora y la mesa.",
    "landing.s2.r2m": "Reservas",
    "landing.s2.r2n": "La misma mesa no se puede tomar dos veces en el mismo horario. Nunca más ese papelón.",
    "landing.s2.r3t": "Escribe \"mila\", pone 3 y toca Enviar.",
    "landing.s2.r3p": "La comanda sale por la comandera de la cocina; la parrilla imprime solo lo suyo. Si después agregan algo, **sale solo lo nuevo**, no la comanda entera otra vez.",
    "landing.s2.r3m": "Comandas",
    "landing.s2.r3n": "Desde el celular del mozo. No hay que comprar tablets ni terminales.",
    "landing.s2.r4t": "Entra un Rappi y no te ensucia el arqueo.",
    "landing.s2.r4p": "Se carga con su canal, el sistema calcula **la comisión y lo que te queda neto**, y lo deja como cobro diferido: esa plata todavía no está en el cajón, así que no entra a la caja de hoy.",
    "landing.s2.r4m": "Delivery",
    "landing.s2.r4n": "Panel \"te deben las apps\": cuánto tiene que depositarte cada una, sin abrir tres paneles distintos.",
    "landing.s2.r5t": "Contás el cajón y la caja te dice sola si cuadra.",
    "landing.s2.r5p": "Compara **medio por medio**, no solo el total: si falta plata, te dice en cuál. Y podés cerrar a ciegas, para que el que cuenta no vea cuánto tendría que haber.",
    "landing.s2.r5m": "Caja",
    "landing.s2.r5n": "El que cierra le manda el reporte al dueño por WhatsApp de un toque: total, mesas, ticket promedio, gastos con nombre y cuánta propina apartar.",
    "landing.s2.r6t": "Al otro día sabés cuánto te deja cada plato.",
    "landing.s2.r6p": "El stock se descontó solo con lo que salió a la cocina anoche. Y en Costos ves el número que importa: **cuánto te queda de cada plato** y a cuánto deberías estar cobrándolo.",
    "landing.s2.r6m": "Costos + Stock",
    "landing.s2.r6n": "\"Actualizado hace 2 meses\" al lado de cada insumo: te delata los precios viejos que te comen el margen.",

    // ── 03 · la comanda ──
    "landing.s3.kicker": "El momento que más se rompe",
    "landing.s3.h2": "Entre que el mozo toma el pedido y la cocina lo lee, se arruinan la mitad de las noches.",
    "landing.s3.lead": "Acá no hay transcripción, no hay letra ilegible y no hay \"yo lo canté\". **El mozo escribe tres letras y el papel sale en la cocina.**",
    "landing.s3.key": "Enviar a cocina",
    "landing.s3.p1t": "Cada categoría sale por la comandera que vos elijas",
    "landing.s3.p1d": "La cocina imprime lo suyo y la parrilla lo suyo. Vos decidís qué sale por dónde, y podés cortar el ticket por sector.",
    "landing.s3.p2t": "Si agregan algo después, sale solo lo nuevo",
    "landing.s3.p2d": "La comanda de agregado no vuelve a imprimir lo que la cocina ya está haciendo. Nadie cocina dos veces el mismo plato.",
    "landing.s3.p3t": "Si la comandera está apagada, el sistema no te miente",
    "landing.s3.p3d": "Esos platos no se dan por enviados. Te avisa antes de que la cocina no cocine, en vez de tragarse el error en silencio.",
    "landing.s3.p4t": "Dos mozos en la misma mesa, sin pisarse",
    "landing.s3.p4d": "El que carga segundo no le borra lo que cargó el primero. Y si se corta internet, la comandera sigue imprimiendo igual.",
    "landing.s3.cmMesa": "Mesa 4",
    "landing.s3.cmSub": "Comanda de cocina · sáb 21:14",
    "landing.s3.cmMozo": "Mozo",
    "landing.s3.cmCub": "Cubiertos",
    "landing.s3.cmNota1": "una sin sal",
    "landing.s3.cmNota2": "sin picante",
    "landing.s3.cmEnviado": "Enviado",
    "landing.s3.cmImpresora": "Impresora",
    "landing.s3.cmStamp": "Impreso en cocina",
    "landing.s3.cmFoot": "El mozo no escribió nada a mano",

    // ── 04 · la cadena ──
    "landing.s4.kicker": "Por qué no dos sistemas",
    "landing.s4.h2": "Un plato. Una carga. Cinco lugares.",
    "landing.s4.lead": "Con una carta digital por un lado y un POS por otro, cargás cada plato dos veces y los precios se desincronizan solos. **Acá eso no puede pasar: no hay dos lugares donde cargarlo.**",
    "landing.s4.origen": "Origen",
    "landing.s4.c1t": "Editor de la carta",
    "landing.s4.c1d": "Nombre, precio, foto, receta. Desde el celular, parado en el salón.",
    "landing.s4.c2t": "Carta QR",
    "landing.s4.c2d": "Lo que ve el comensal cuando se sienta.",
    "landing.s4.c3t": "PDF impreso",
    "landing.s4.c3d": "La carta de papel, lista para la imprenta.",
    "landing.s4.c4t": "Comandas",
    "landing.s4.c4d": "La pantalla del mozo y el ticket de cocina.",
    "landing.s4.c5t": "Costos y stock",
    "landing.s4.c5d": "Qué te deja y qué te queda en la heladera.",
    "landing.s4.foot": "Si ya tenés tu carta con nosotros, tus platos aparecen en comandas sin que recargues uno solo. Ni siquiera los de las cartas de hace cinco años.",
    "landing.s4.swipe": "Deslizá para ver los cinco",

    // ── 05 · módulos ──
    "landing.s5.kicker": "El sistema completo",
    "landing.s5.h2": "Siete módulos. Una sola cuenta y un solo lugar donde mirar.",
    "landing.s5.thMod": "Módulo",
    "landing.s5.thQue": "Qué resuelve",
    "landing.s5.thPlan": "Plan",
    "landing.s5.planCarta": "Plan Carta",
    "landing.s5.planPack": "Pack Gestión",
    "landing.s5.m1n": "Carta digital",
    "landing.s5.m1d": "Carta a medida de tu marca con QR, en español, inglés y portugués. Plato del día, ofertas, horarios, anuncios y categorías que aparecen solas según la hora. Abre en menos de un segundo y el comensal no instala nada.",
    "landing.s5.m2n": "Reservas",
    "landing.s5.m2d": "Tu link propio de reservas: el cliente elige mesa tocando el plano de tu salón. Vos manejás la noche desde la pantalla \"Hoy\", con walk-ins, lista de espera y aviso por WhatsApp.",
    "landing.s5.m3n": "Costos y ganancias",
    "landing.s5.m3d": "Cargás la receta y te dice cuánto te deja el plato, con semáforo y con el precio al que deberías cobrarlo. Trae más de 100 insumos argentinos ya precargados con sus unidades.",
    "landing.s5.m4n": "PDF de la carta",
    "landing.s5.m4d": "La misma carta lista para imprimir en papel, con editor manual del corte de página para cuando un plato queda partido al final de la hoja.",
    "landing.s5.m5n": "Comandas / POS",
    "landing.s5.m5d": "Pedido por mesa desde el celular del mozo, comanda impresa por sector, cobro con varios medios a la vez, cuenta dividida, propinas por mozo y mesas que se juntan, se mudan o se reabren. Incluye los pedidos de Rappi y PedidosYa, con su comisión y su neto calculados aparte.",
    "landing.s5.m6n": "Caja / arqueo",
    "landing.s5.m6d": "Cierre que compara medio por medio, cierre a ciegas, gastos y retiros con nombre y monto, y el reporte completo de la noche listo para mandarle al dueño por WhatsApp.",
    "landing.s5.m7n": "Stock e insumos",
    "landing.s5.m7d": "Montado sobre las recetas que ya cargaste en Costos: lo que se vende descuenta lo que se usa, y te avisa el faltante en el momento en que el mozo carga el pedido.",

    // ── 06 · prueba ──
    "landing.s6.kicker": "Quién ya lo usa",
    "landing.s6.h2": "De Jujuy a Santa Cruz, abriendo esta noche.",
    "landing.s6.lead": "Parrillas, sushi, hoteles boutique, foodtrucks y bares de barrio en 21 provincias y en Uruguay. Todas estas cartas están publicadas y funcionando ahora mismo: **acá no hay pantallas de mentira.**",
    "landing.s6.gTag": "Google · reseñas verificadas",
    "landing.s6.gNote": "Sin una sola reseña floja. Están todas en el perfil de Google de RestaurAdmin, con nombre y apellido de quien la escribió.",
    "landing.s6.gLink": "Leer las reseñas",
    "landing.s6.more": "+190 más",

    // ── 07 · la cuenta ──
    "landing.s7.kicker": "Hagamos la cuenta",
    "landing.s7.h2": "No te vamos a prometer que vas a vender un 30% más.",
    "landing.s7.lead": "No tenemos cómo demostrártelo y vos ya escuchaste esa frase mil veces. Lo único honesto que podemos hacer es esta cuenta: **cuánto factura tu local, y qué parte de eso es el sistema.**",
    "landing.s7.calcTitle": "Tu local, en tres números",
    "landing.s7.calcSub": "Poné los tuyos. No se guarda nada, no te pedimos el mail y no te va a llamar nadie por esto.",
    "landing.s7.fMesas": "Mesas por noche",
    "landing.s7.fTicket": "Ticket promedio",
    "landing.s7.fNoches": "Noches por semana",
    "landing.s7.dispLabel": "Facturación mensual estimada",
    "landing.s7.dispSub": "Sobre {mesas} mesas al mes, con tus propios números.",
    "landing.s7.rowInstal": "Costo de instalación",
    "landing.s7.rowPerm": "Permanencia mínima",
    "landing.s7.rowPermVal": "ninguna",
    "landing.s7.punch1": "El sistema completo te sale lo mismo que una mesa al mes.",
    "landing.s7.punchN": "El sistema completo te sale lo mismo que {n} mesas al mes.",
    "landing.s7.punchSub": "Si una sola noche te cuadra la caja que hoy no cuadra, ya se pagó.",
    "landing.s7.fine": "Estimación con tus propios números: mesas × ticket × noches × 4,33 semanas.\nNo es una promesa de ventas: es lo que ya facturás.",

    // ── 08 · precios ──
    "landing.s8.kicker": "Precios",
    "landing.s8.h2": "Dos planes. Sin letra chica y sin vendedor.",
    "landing.s8.lead": "Suscripción mensual, sin costo de instalación y sin permanencia. Se pausa cuando quieras y no perdés nada: los datos quedan y al reactivar vuelve todo como estaba.",
    "landing.s8.mes": "/ mes",
    "landing.s8.p1k": "01 · Plan Carta",
    "landing.s8.p1t": "La carta, ordenada.",
    "landing.s8.p1who": "Para el que quiere una carta digital seria, tomar reservas y saber cuánto le deja cada plato.",
    "landing.s8.p1note": "ARS · sin instalación · sin permanencia",
    "landing.s8.p1f1": "Carta digital a medida + QR",
    "landing.s8.p1f2": "Sistema de reservas completo",
    "landing.s8.p1f3": "Costos y ganancias por plato",
    "landing.s8.p1f4": "PDF de la carta para imprimir",
    "landing.s8.p1f5": "Comandas, caja y stock",
    "landing.s8.p1cta": "Empezar por la carta",
    "landing.s8.p2tag": "El local entero",
    "landing.s8.p2k": "02 · Plan Completo",
    "landing.s8.p2t": "El local, ordenado.",
    "landing.s8.p2who": "Todo lo de arriba más el pack Gestión: el salón, la cocina y la caja adentro del mismo sistema.",
    "landing.s8.p2note": "ARS · te dejamos la comandera funcionando",
    "landing.s8.p2f1": "Todo el Plan Carta",
    "landing.s8.p2f2": "Comandas / POS por mesa",
    "landing.s8.p2f3": "Impresión de comandas y cuentas",
    "landing.s8.p2f4": "Caja y arqueo con reporte por WhatsApp",
    "landing.s8.p2f5": "Pedidos de apps de delivery",
    "landing.s8.p2f6": "Stock e insumos",
    "landing.s8.p2cta": "Quiero el sistema completo",

    // ── 09 · cómo empezás ──
    "landing.s9.kicker": "Cómo empezás",
    "landing.s9.h2": "Vos no tocás nada hasta que esté lista.",
    "landing.s9.lead": "El trabajo de cargar la carta lo hacemos nosotros. Vos la revisás cuando ya está hecha.",
    "landing.s9.step": "Paso",
    "landing.s9.s1t": "Nos mandás tu carta",
    "landing.s9.s1d": "Como la tengas: un PDF, una foto del papel, un Excel o el link de la que ya usás. La cargamos nosotros, plato por plato, con fotos y categorías.",
    "landing.s9.s1w": "Día 1",
    "landing.s9.s2t": "La revisás desde tu celular",
    "landing.s9.s2d": "Te pasamos el link para que la mires como la va a ver tu cliente. Corregís lo que quieras: desde ese momento la editás vos, sin pedirle permiso a nadie.",
    "landing.s9.s2w": "Día 2",
    "landing.s9.s3t": "Prendemos lo que necesites",
    "landing.s9.s3d": "Si vas por el pack Gestión, dejamos la comandera imprimiendo y los celulares de los mozos conectados. Presencial o remoto, pero lo dejamos andando.",
    "landing.s9.s3w": "Esa semana",

    // ── 10 · dudas ──
    "landing.s10.kicker": "Las dudas de siempre",
    "landing.s10.h2": "Lo que te frena, contestado derecho.",
    "landing.s10.q1": "\"¿Emite factura?\"",
    "landing.s10.a1": "**No, y preferimos decírtelo antes que después.** La cuenta sale con la leyenda \"documento no válido como factura\" y el cierre del día es un reporte de control interno. Para lo fiscal seguís con lo que ya usás; esto te ordena la operación, no te reemplaza el facturador.",
    "landing.s10.q2": "\"Se me cae internet en el medio del servicio.\"",
    "landing.s10.a2": "Comandas sigue funcionando en la red del local: los mozos siguen cargando y la comandera sigue imprimiendo como si nada. Cuando vuelve la conexión se sincroniza solo, sin que nadie tenga que hacer nada.",
    "landing.s10.q3": "\"La computadora del local es una reliquia.\"",
    "landing.s10.a3": "El agente de impresión anda hasta en Windows 7. Es un solo archivo: doble clic y queda instalado como servicio, arrancando solo cada vez que prendés la máquina. No hace falta comprar nada nuevo.",
    "landing.s10.q4": "\"Mis mozos no van a saber usarlo.\"",
    "landing.s10.a4": "Escriben las primeras letras del plato, ponen la cantidad y aprietan una tecla. El que sabe mandar un WhatsApp lo maneja la primera noche. Y usan su propio celular: no hay que comprar terminales.",
    "landing.s10.q5": "\"Ya tengo la carta cargada con ustedes.\"",
    "landing.s10.a5": "Entonces tenés el 80% del trabajo hecho. Se activa el pack Gestión sobre tu misma cuenta y tus platos aparecen en comandas sin recargar uno solo, incluso si tu carta es de hace cinco años.",
    "landing.s10.q6": "\"¿Y si arranco y no me sirve?\"",
    "landing.s10.a6": "Se pausa cuando quieras y los datos quedan intactos. Al reactivar vuelve todo como estaba. No hay contrato de permanencia, no hay costo de salida y no te vamos a hacer llamar por nadie para retenerte.",

    // ── total / cierre ──
    "landing.cta.kicker": "El próximo paso",
    "landing.cta.h2a": "Contanos de tu local y",
    "landing.cta.h2b": "arrancamos",
    "landing.cta.h2c": "esta semana.",
    "landing.cta.lead": "Nos escribís, te decimos qué plan te sirve y qué necesita tu local. Si arrancás, la carta la cargamos nosotros: vos la revisás cuando ya está hecha.",
    "landing.cta.wa": "Escribinos por WhatsApp",
    "landing.cta.mail": "Mandanos un mail",
    "landing.cta.fine": "Sin permanencia y sin que te llame un vendedor.",
    "landing.cta.formTitle": "O dejanos tu teléfono y te llamamos",
    "landing.cta.formName": "Tu nombre",
    "landing.cta.formBiz": "Nombre del local",
    "landing.cta.formTel": "Teléfono",
    "landing.cta.formSend": "Que me llamen",
    "landing.cta.formSending": "Enviando…",
    "landing.cta.formOk": "Listo. Te escribimos hoy mismo.",
    "landing.cta.formErr": "No se pudo enviar. Escribinos por WhatsApp y lo resolvemos.",
    "landing.rc.carta": "Carta digital + QR",
    "landing.rc.reservas": "Reservas",
    "landing.rc.costos": "Costos y ganancias",
    "landing.rc.pdf": "PDF de la carta",
    "landing.rc.pack": "Comandas + Caja + Stock",
    "landing.rc.incluida": "incluida",
    "landing.rc.incluidas": "incluidas",
    "landing.rc.incluidos": "incluidos",
    "landing.rc.incluido": "incluido",
    "landing.rc.packVal": "pack Gestión",
    "landing.rc.carga": "Carga de la carta",
    "landing.rc.cargaVal": "la hacemos nosotros",
    "landing.rc.instal": "Instalación",
    "landing.rc.perm": "Permanencia",
    "landing.rc.permVal": "ninguna",
    "landing.rc.desde": "Desde",

    // ── footer ──
    "landing.foot.desc": "El sistema de gestión para restaurantes argentinos. Hecho en Buenos Aires y sostenido por gente que atiende el teléfono.",
    "landing.foot.producto": "Producto",
    "landing.foot.modulos": "Los módulos",
    "landing.foot.cuenta": "Hacé la cuenta",
    "landing.foot.precios": "Precios",
    "landing.foot.empezar": "Cómo empezás",
    "landing.foot.contacto": "Contacto",
    "landing.foot.legal": "Legal",
    "landing.foot.priv": "Privacidad",
    "landing.foot.terms": "Términos",
    "landing.foot.data": "Borrado de datos",
    "landing.foot.iva": "Precios en pesos argentinos · IVA incluido",
    "landing.dock.desde": "Desde {precio}/mes",
    "landing.dock.sin": "sin permanencia",
    "landing.dock.cta": "Hablemos",
    "landing.wa.msg": "Hola, quiero ver RestaurAdmin con mi carta.",
  },

  en: {
    "landing.nav.noche": "The night",
    "landing.nav.sistema": "The system",
    "landing.nav.cuenta": "The math",
    "landing.nav.precios": "Pricing",
    "landing.nav.cta": "Let's talk",
    "landing.nav.aria": "Page sections",

    "landing.hero.kicker": "Restaurant management system",
    "landing.hero.t1": "At the end of",
    "landing.hero.t2": "the night, it all",
    "landing.hero.t3": "comes down to",
    "landing.hero.t4": "one number.",
    "landing.hero.sub": "And today that number comes out of a notebook, the manager's memory and a calculator nobody double-checks. **RestaurAdmin goes with you from the menu to the cash count** so it comes out on its own — and comes out right.",
    "landing.hero.ctaPrimary": "I want to get started",
    "landing.hero.ctaSecondary": "Do the math yourself",
    "landing.hero.m1": "restaurants",
    "landing.hero.m2": "provinces",
    "landing.hero.m3": "on Google",
    "landing.hero.m4": "from {precio}/month",

    "landing.tk.close": "Cash close · Sat 23:52",
    "landing.tk.mesas": "Tables paid",
    "landing.tk.cubiertos": "Covers",
    "landing.tk.ticket": "Average check",
    "landing.tk.efectivo": "Cash",
    "landing.tk.debito": "Debit",
    "landing.tk.credito": "Credit",
    "landing.tk.transferencia": "Bank transfer",
    "landing.tk.total": "Total",
    "landing.tk.dif": "Cash difference",
    "landing.tk.cuadra": "The drawer balances",
    "landing.tk.propina": "Tips to split",
    "landing.tk.apps": "The apps owe you",
    "landing.tk.legal1": "Internal control report",
    "landing.tk.legal2": "Not valid as an invoice",
    "landing.tk.cap": "Saturday's close,\nthe way the owner sees it.",

    "landing.band.l1": "Active restaurants",
    "landing.band.l2": "Provinces, plus Uruguay",
    "landing.band.l3": "Stars on Google",
    "landing.band.l4": "Excel spreadsheets on the side",

    "landing.s1.kicker": "The problem",
    "landing.s1.h2": "It's not that you don't know how much you sold. It's that you find out late, and only half of it.",
    "landing.s1.lead": "Your restaurant's numbers do exist. The problem is they're spread across five places that don't talk to each other, and the one who has to put them together at the end of the month is you.",
    "landing.s1.aHead": "How you do it today",
    "landing.s1.aTitle": "Five places\nthat don't talk.",
    "landing.s1.a1": "The order ticket gets written by hand and someone types it up when they get a chance.",
    "landing.s1.a2": "Your costs live in a spreadsheet you last updated in March.",
    "landing.s1.a3": "You send the new price on WhatsApp to whoever made your menu, and you wait.",
    "landing.s1.a4": "The cash count is done from memory: if money is missing, you find out next week.",
    "landing.s1.a5": "You pulled a dish off the kitchen line and the printed menu is still offering it.",
    "landing.s1.aFoot": "Five places. Not one number you can trust.",
    "landing.s1.bHead": "How it ends up",
    "landing.s1.bTitle": "One single place,\nand the number comes out on its own.",
    "landing.s1.b1": "The order is entered once, at the table, and prints in the kitchen.",
    "landing.s1.b2": "Each dish's cost works itself out from the recipe you entered once.",
    "landing.s1.b3": "You change the price from your phone and it's already on the QR at the table.",
    "landing.s1.b4": "The cash close compares method by method and tells you if it balances before you leave.",
    "landing.s1.b5": "You pull a dish and it disappears from the menu, the PDF and the POS at the same time.",
    "landing.s1.bFoot": "One place. One number.",

    "landing.s2.kicker": "The night, line by line",
    "landing.s2.h2": "All of this is going to happen anyway. The difference is who writes it down.",
    "landing.s2.lead": "These aren't loose features in a grid of cards: it's your Saturday, hour by hour, and what the system does on each line.",
    "landing.s2.who1": "You",
    "landing.s2.who2": "The server",
    "landing.s2.who3": "The register",
    "landing.s2.r1t": "You raise the price of the milanesa from your phone.",
    "landing.s2.r1p": "You tap the price in the list, hit **Publish menu** and whoever scans the QR two minutes later already sees the new one. No print shop, no reprinting the QR, no depending on anyone.",
    "landing.s2.r1m": "Digital menu",
    "landing.s2.r1n": "You're out of fish: tap the eye and it disappears from the menu without losing the photo, the price or the recipe.",
    "landing.s2.r2t": "A reservation for four comes in and your phone tells you.",
    "landing.s2.r2p": "It arrives as a notification on your phone **even with the app closed**. You confirm with one tap, the system gives them the smallest table that group fits at, and WhatsApp opens with the confirmation already written: the name, the time and the table.",
    "landing.s2.r2m": "Reservations",
    "landing.s2.r2n": "The same table can't be booked twice for the same time. Never that embarrassment again.",
    "landing.s2.r3t": "They type \"mila\", enter 3 and tap Send.",
    "landing.s2.r3p": "The ticket prints on the kitchen printer; the grill prints only its own items. If they add something later, **only the new items print**, not the whole ticket again.",
    "landing.s2.r3m": "Orders",
    "landing.s2.r3n": "From the server's own phone. No tablets or terminals to buy.",
    "landing.s2.r4t": "A Rappi order comes in and doesn't mess up your cash count.",
    "landing.s2.r4p": "It's entered with its channel, the system works out **the commission and what you actually keep**, and leaves it as a deferred payment: that money isn't in the drawer yet, so it doesn't go into today's cash count.",
    "landing.s2.r4m": "Delivery",
    "landing.s2.r4n": "An \"apps owe you\" panel: how much each one has to deposit, without opening three different dashboards.",
    "landing.s2.r5t": "You count the drawer and the cash close tells you on its own if it balances.",
    "landing.s2.r5p": "It compares **method by method**, not just the total: if money is missing, it tells you which one. And you can run a blind close, so whoever counts doesn't see how much should be there.",
    "landing.s2.r5m": "Cash close",
    "landing.s2.r5n": "Whoever closes sends the owner the report on WhatsApp with one tap: total, tables, average check, expenses by name and how much tip money to set aside.",
    "landing.s2.r6t": "The next day you know what each dish makes you.",
    "landing.s2.r6p": "Stock already came down on its own from what went out of the kitchen last night. And in Costs you see the number that matters: **what you keep on each dish** and what you should be charging for it.",
    "landing.s2.r6m": "Costs + Stock",
    "landing.s2.r6n": "\"Updated 2 months ago\" next to each ingredient: it flags the stale prices that are eating your margin.",

    "landing.s3.kicker": "The moment that breaks most often",
    "landing.s3.h2": "Between the server taking the order and the kitchen reading it, half the nights fall apart.",
    "landing.s3.lead": "Nothing gets copied out, there's no unreadable handwriting and no \"I called it out\". **The server types three letters and the paper comes out in the kitchen.**",
    "landing.s3.key": "Send to kitchen",
    "landing.s3.p1t": "Each category prints on the printer you choose",
    "landing.s3.p1d": "The kitchen prints its own items and the grill prints its own. You decide what goes where, and you can split the ticket by station.",
    "landing.s3.p2t": "If they add something later, only the new items print",
    "landing.s3.p2d": "The add-on ticket doesn't reprint what the kitchen is already cooking. Nobody cooks the same dish twice.",
    "landing.s3.p3t": "If the printer is off, the system doesn't lie to you",
    "landing.s3.p3d": "Those dishes aren't counted as sent. It warns you before the kitchen doesn't cook them, instead of swallowing the error in silence.",
    "landing.s3.p4t": "Two servers on the same table, without stepping on each other",
    "landing.s3.p4d": "The second one to enter an order doesn't wipe out what the first one entered. And if the internet drops, the printer keeps printing anyway.",
    "landing.s3.cmMesa": "Table 4",
    "landing.s3.cmSub": "Kitchen ticket · Sat 21:14",
    "landing.s3.cmMozo": "Server",
    "landing.s3.cmCub": "Covers",
    "landing.s3.cmNota1": "one with no salt",
    "landing.s3.cmNota2": "no spice",
    "landing.s3.cmEnviado": "Sent",
    "landing.s3.cmImpresora": "Printer",
    "landing.s3.cmStamp": "Printed in the kitchen",
    "landing.s3.cmFoot": "The server didn't write a thing by hand",

    "landing.s4.kicker": "Why not two systems",
    "landing.s4.h2": "One dish. Entered once. Five places.",
    "landing.s4.lead": "With a digital menu on one side and a POS on the other, you enter every dish twice and the prices drift apart on their own. **That can't happen here: there aren't two places to enter it.**",
    "landing.s4.origen": "Source",
    "landing.s4.c1t": "Menu editor",
    "landing.s4.c1d": "Name, price, photo, recipe. From your phone, standing in the dining room.",
    "landing.s4.c2t": "QR menu",
    "landing.s4.c2d": "What the guest sees when they sit down.",
    "landing.s4.c3t": "Printed PDF",
    "landing.s4.c3d": "The paper menu, ready for the print shop.",
    "landing.s4.c4t": "Orders",
    "landing.s4.c4d": "The server's screen and the kitchen ticket.",
    "landing.s4.c5t": "Costs and stock",
    "landing.s4.c5d": "What it makes you and what's left in the fridge.",
    "landing.s4.foot": "If your menu is already with us, your dishes show up in Orders without you re-entering a single one. Not even the ones from menus five years old.",
    "landing.s4.swipe": "Swipe to see all five",

    "landing.s5.kicker": "The whole system",
    "landing.s5.h2": "Seven modules. One account and one place to look.",
    "landing.s5.thMod": "Module",
    "landing.s5.thQue": "What it solves",
    "landing.s5.thPlan": "Plan",
    "landing.s5.planCarta": "Menu Plan",
    "landing.s5.planPack": "Management Pack",
    "landing.s5.m1n": "Digital menu",
    "landing.s5.m1d": "A menu built to match your brand, with a QR, in Spanish, English and Portuguese. Dish of the day, specials, hours, announcements and categories that show up on their own depending on the time. It opens in under a second and the guest installs nothing.",
    "landing.s5.m2n": "Reservations",
    "landing.s5.m2d": "Your own booking link: the guest picks a table by tapping the floor plan of your dining room. You run the night from the \"Today\" screen, with walk-ins, a waitlist and WhatsApp notices.",
    "landing.s5.m3n": "Costs and profit",
    "landing.s5.m3d": "You enter the recipe and it tells you what the dish makes you, with a traffic light and the price you should be charging for it. It comes with over 100 Argentine ingredients already loaded with their units.",
    "landing.s5.m4n": "Menu PDF",
    "landing.s5.m4d": "The same menu ready to print on paper, with a manual page-break editor for when a dish gets split at the bottom of the page.",
    "landing.s5.m5n": "Orders / POS",
    "landing.s5.m5d": "Orders by table from the server's phone, tickets printed by station, payment with several methods at once, split checks, tips by server and tables that get joined, moved or reopened. It includes Rappi and PedidosYa orders, with their commission and net worked out separately.",
    "landing.s5.m6n": "Cash / close-out",
    "landing.s5.m6d": "A close that compares method by method, blind close, expenses and cash-outs with name and amount, and the full report of the night ready to send the owner on WhatsApp.",
    "landing.s5.m7n": "Stock and ingredients",
    "landing.s5.m7d": "Built on the recipes you already entered in Costs: what sells takes down what it uses, and it tells you what's missing the moment the server enters the order.",

    "landing.s6.kicker": "Who already uses it",
    "landing.s6.h2": "From Jujuy to Santa Cruz, opening tonight.",
    "landing.s6.lead": "Steakhouses, sushi places, boutique hotels, food trucks and neighborhood bars in 21 provinces and in Uruguay. Every one of these menus is published and running right now: **no fake screens here.**",
    "landing.s6.gTag": "Google · verified reviews",
    "landing.s6.gNote": "Not one weak review. They're all on RestaurAdmin's Google profile, with the full name of whoever wrote it.",
    "landing.s6.gLink": "Read the reviews",
    "landing.s6.more": "+190 more",

    "landing.s7.kicker": "Let's do the math",
    "landing.s7.h2": "We're not going to promise you'll sell 30% more.",
    "landing.s7.lead": "We have no way to prove it to you and you've heard that line a thousand times. The only honest thing we can do is this math: **how much your restaurant bills, and what part of that the system is.**",
    "landing.s7.calcTitle": "Your restaurant, in three numbers",
    "landing.s7.calcSub": "Put in your own. Nothing is saved, we don't ask for your email and nobody is going to call you over this.",
    "landing.s7.fMesas": "Tables per night",
    "landing.s7.fTicket": "Average check",
    "landing.s7.fNoches": "Nights per week",
    "landing.s7.dispLabel": "Estimated monthly revenue",
    "landing.s7.dispSub": "On {mesas} tables a month, with your own numbers.",
    "landing.s7.rowInstal": "Setup cost",
    "landing.s7.rowPerm": "Minimum commitment",
    "landing.s7.rowPermVal": "none",
    "landing.s7.punch1": "The whole system costs you the same as one table a month.",
    "landing.s7.punchN": "The whole system costs you the same as {n} tables a month.",
    "landing.s7.punchSub": "If it balances the drawer on one single night that doesn't balance today, it already paid for itself.",
    "landing.s7.fine": "An estimate with your own numbers: tables × check × nights × 4.33 weeks.\nIt's not a sales promise: it's what you already bill.",

    "landing.s8.kicker": "Pricing",
    "landing.s8.h2": "Two plans. No fine print and no salesperson.",
    "landing.s8.lead": "Monthly subscription, no setup cost and no commitment. Pause it whenever you want and you lose nothing: your data stays and when you turn it back on everything comes back the way it was.",
    "landing.s8.mes": "/ month",
    "landing.s8.p1k": "01 · Menu Plan",
    "landing.s8.p1t": "The menu, in order.",
    "landing.s8.p1who": "For anyone who wants a serious digital menu, to take reservations and to know what each dish makes them.",
    "landing.s8.p1note": "ARS · no setup · no commitment",
    "landing.s8.p1f1": "Custom digital menu + QR",
    "landing.s8.p1f2": "Full reservation system",
    "landing.s8.p1f3": "Costs and profit by dish",
    "landing.s8.p1f4": "Menu PDF for printing",
    "landing.s8.p1f5": "Orders, cash close and stock",
    "landing.s8.p1cta": "Start with the menu",
    "landing.s8.p2tag": "The whole restaurant",
    "landing.s8.p2k": "02 · Complete Plan",
    "landing.s8.p2t": "The restaurant, in order.",
    "landing.s8.p2who": "Everything above plus the Management pack: the dining room, the kitchen and the register inside the same system.",
    "landing.s8.p2note": "ARS · we leave your ticket printer working",
    "landing.s8.p2f1": "Everything in the Menu Plan",
    "landing.s8.p2f2": "Orders / POS by table",
    "landing.s8.p2f3": "Printing of kitchen tickets and checks",
    "landing.s8.p2f4": "Cash close with a WhatsApp report",
    "landing.s8.p2f5": "Delivery app orders",
    "landing.s8.p2f6": "Stock and ingredients",
    "landing.s8.p2cta": "I want the whole system",

    "landing.s9.kicker": "How you start",
    "landing.s9.h2": "You don't touch a thing until it's ready.",
    "landing.s9.lead": "We do the work of loading the menu. You review it once it's done.",
    "landing.s9.step": "Step",
    "landing.s9.s1t": "You send us your menu",
    "landing.s9.s1d": "However you have it: a PDF, a photo of the paper one, an Excel file or the link to the one you already use. We load it ourselves, dish by dish, with photos and categories.",
    "landing.s9.s1w": "Day 1",
    "landing.s9.s2t": "You review it from your phone",
    "landing.s9.s2d": "We send you the link so you can look at it the way your guest will. Fix whatever you want: from that moment on you edit it yourself, without asking anyone.",
    "landing.s9.s2w": "Day 2",
    "landing.s9.s3t": "We turn on whatever you need",
    "landing.s9.s3d": "If you go with the Management pack, we leave the ticket printer printing and the servers' phones connected. In person or remotely, but we leave it running.",
    "landing.s9.s3w": "That same week",

    "landing.s10.kicker": "The usual doubts",
    "landing.s10.h2": "What's holding you back, answered straight.",
    "landing.s10.q1": "\"Does it issue invoices?\"",
    "landing.s10.a1": "**No, and we'd rather tell you now than later.** The check prints with the line \"document not valid as an invoice\" and the daily close is an internal control report. For anything tax-related you keep using what you use today; this puts your operation in order, it doesn't replace your invoicing software.",
    "landing.s10.q2": "\"My internet drops in the middle of service.\"",
    "landing.s10.a2": "Orders keeps working on the restaurant's own network: the servers keep entering orders and the printer keeps printing like nothing happened. When the connection comes back it syncs on its own, without anyone having to do a thing.",
    "landing.s10.q3": "\"The computer at my place is a relic.\"",
    "landing.s10.a3": "The printing agent runs even on Windows 7. It's a single file: double-click and it installs as a service, starting on its own every time you turn the machine on. You don't need to buy anything new.",
    "landing.s10.q4": "\"My servers won't know how to use it.\"",
    "landing.s10.a4": "They type the first letters of the dish, enter the quantity and hit one key. Anyone who can send a WhatsApp has it down the first night. And they use their own phone: no terminals to buy.",
    "landing.s10.q5": "\"I already have my menu loaded with you.\"",
    "landing.s10.a5": "Then you have 80% of the work done. The Management pack turns on over that same account and your dishes show up in Orders without re-entering a single one, even if your menu is five years old.",
    "landing.s10.q6": "\"And what if I start and it's no use to me?\"",
    "landing.s10.a6": "Pause it whenever you want and the data stays intact. When you turn it back on everything comes back as it was. There's no lock-in contract, no exit fee and we're not going to have anyone call you to keep you.",

    "landing.cta.kicker": "The next step",
    "landing.cta.h2a": "Tell us about your place and",
    "landing.cta.h2b": "we get started",
    "landing.cta.h2c": "this week.",
    "landing.cta.lead": "Write to us and we'll tell you which plan fits and what your place needs. If you go ahead, we load the menu ourselves: you just review it once it is done.",
    "landing.cta.wa": "Message us on WhatsApp",
    "landing.cta.mail": "Send us an email",
    "landing.cta.fine": "No lock-in and no salesperson calling you.",
    "landing.cta.formTitle": "Or leave us your phone and we'll call you",
    "landing.cta.formName": "Your name",
    "landing.cta.formBiz": "Restaurant name",
    "landing.cta.formTel": "Phone",
    "landing.cta.formSend": "Call me",
    "landing.cta.formSending": "Sending…",
    "landing.cta.formOk": "Done. We'll get in touch today.",
    "landing.cta.formErr": "It couldn't be sent. Message us on WhatsApp and we'll sort it out.",
    "landing.rc.carta": "Digital menu + QR",
    "landing.rc.reservas": "Reservations",
    "landing.rc.costos": "Costs and profit",
    "landing.rc.pdf": "Menu PDF",
    "landing.rc.pack": "Orders + Cash close + Stock",
    "landing.rc.incluida": "included",
    "landing.rc.incluidas": "included",
    "landing.rc.incluidos": "included",
    "landing.rc.incluido": "included",
    "landing.rc.packVal": "Management pack",
    "landing.rc.carga": "Loading your menu",
    "landing.rc.cargaVal": "we do it for you",
    "landing.rc.instal": "Setup",
    "landing.rc.perm": "Commitment",
    "landing.rc.permVal": "none",
    "landing.rc.desde": "From",

    "landing.foot.desc": "The management system for Argentine restaurants. Made in Buenos Aires and backed by people who answer the phone.",
    "landing.foot.producto": "Product",
    "landing.foot.modulos": "The modules",
    "landing.foot.cuenta": "Do the math",
    "landing.foot.precios": "Pricing",
    "landing.foot.empezar": "How you start",
    "landing.foot.contacto": "Contact",
    "landing.foot.legal": "Legal",
    "landing.foot.priv": "Privacy",
    "landing.foot.terms": "Terms",
    "landing.foot.data": "Data deletion",
    "landing.foot.iva": "Prices in Argentine pesos · tax included",
    "landing.dock.desde": "From {precio}/month",
    "landing.dock.sin": "no commitment",
    "landing.dock.cta": "Let's talk",
    "landing.wa.msg": "Hi, I'd like to see RestaurAdmin with my menu.",
  },

  pt: {
    "landing.nav.noche": "A noite",
    "landing.nav.sistema": "O sistema",
    "landing.nav.cuenta": "As contas",
    "landing.nav.precios": "Preços",
    "landing.nav.cta": "Vamos conversar",
    "landing.nav.aria": "Seções da página",

    "landing.hero.kicker": "Sistema de gestão para restaurantes",
    "landing.hero.t1": "No fim da",
    "landing.hero.t2": "noite, tudo",
    "landing.hero.t3": "se resume a",
    "landing.hero.t4": "um número.",
    "landing.hero.sub": "E hoje esse número sai de um caderno, da memória do gerente e de uma calculadora que ninguém confere. **O RestaurAdmin acompanha você do cardápio ao caixa** para que ele saia sozinho — e seja o correto.",
    "landing.hero.ctaPrimary": "Quero começar",
    "landing.hero.ctaSecondary": "Faça as contas você mesmo",
    "landing.hero.m1": "restaurantes",
    "landing.hero.m2": "províncias",
    "landing.hero.m3": "no Google",
    "landing.hero.m4": "a partir de {precio}/mês",

    "landing.tk.close": "Fechamento de caixa · sáb 23:52",
    "landing.tk.mesas": "Mesas fechadas",
    "landing.tk.cubiertos": "Pessoas",
    "landing.tk.ticket": "Ticket médio",
    "landing.tk.efectivo": "Dinheiro",
    "landing.tk.debito": "Débito",
    "landing.tk.credito": "Crédito",
    "landing.tk.transferencia": "Transferência",
    "landing.tk.total": "Total",
    "landing.tk.dif": "Diferença de caixa",
    "landing.tk.cuadra": "O caixa bate",
    "landing.tk.propina": "Gorjeta a dividir",
    "landing.tk.apps": "Os apps te devem",
    "landing.tk.legal1": "Relatório de controle interno",
    "landing.tk.legal2": "Não vale como nota fiscal",
    "landing.tk.cap": "O fechamento de sábado,\ncomo o dono vê.",

    "landing.band.l1": "Restaurantes ativos",
    "landing.band.l2": "Províncias, mais o Uruguai",
    "landing.band.l3": "Estrelas no Google",
    "landing.band.l4": "Planilhas de Excel em paralelo",

    "landing.s1.kicker": "O problema",
    "landing.s1.h2": "Não é que você não saiba quanto vendeu. É que você fica sabendo tarde, e pela metade.",
    "landing.s1.lead": "Os dados do seu restaurante existem. O problema é que estão espalhados em cinco lugares que não se falam, e quem tem que juntar tudo no fim do mês é você.",
    "landing.s1.aHead": "Como você faz hoje",
    "landing.s1.aTitle": "Cinco lugares\nque não se falam.",
    "landing.s1.a1": "A comanda é escrita à mão e alguém transcreve quando dá.",
    "landing.s1.a2": "Os custos vivem numa planilha que você atualizou pela última vez em março.",
    "landing.s1.a3": "O preço novo você manda por WhatsApp para quem fez o seu cardápio, e espera.",
    "landing.s1.a4": "O fechamento de caixa é feito de memória: se falta dinheiro, você descobre na semana que vem.",
    "landing.s1.a5": "Você tirou um prato da cozinha e o cardápio impresso continua oferecendo ele.",
    "landing.s1.aFoot": "Cinco lugares. Nenhum número confiável.",
    "landing.s1.bHead": "Como fica",
    "landing.s1.bTitle": "Um lugar só,\ne o número sai sozinho.",
    "landing.s1.b1": "O pedido é lançado uma vez, na mesa, e sai impresso na cozinha.",
    "landing.s1.b2": "O custo de cada prato é calculado sozinho com a receita que você cadastrou uma vez.",
    "landing.s1.b3": "O preço você muda pelo celular e ele já está no QR da mesa.",
    "landing.s1.b4": "O caixa compara meio a meio e te diz se bate antes de você ir embora.",
    "landing.s1.b5": "Você tira um prato e ele some do cardápio, do PDF e do POS ao mesmo tempo.",
    "landing.s1.bFoot": "Um lugar só. Um número só.",

    "landing.s2.kicker": "A noite, linha por linha",
    "landing.s2.h2": "Tudo isso vai acontecer de qualquer jeito. A diferença é quem anota.",
    "landing.s2.lead": "Não são funções soltas numa grade de cards: é o seu sábado, hora a hora, e o que o sistema faz em cada linha.",
    "landing.s2.who1": "Você",
    "landing.s2.who2": "O garçom",
    "landing.s2.who3": "O caixa",
    "landing.s2.r1t": "Você aumenta o preço da milanesa pelo celular.",
    "landing.s2.r1p": "Você toca no preço da lista, aperta **Publicar cardápio** e quem escanear o QR dois minutos depois já vê o novo. Sem gráfica, sem reimprimir o QR, sem depender de ninguém.",
    "landing.s2.r1m": "Cardápio digital",
    "landing.s2.r1n": "Acabou o peixe: você toca no olho e ele some do cardápio sem perder a foto, o preço nem a receita.",
    "landing.s2.r2t": "Entra uma reserva para quatro e o telefone te avisa.",
    "landing.s2.r2p": "Chega como notificação no celular **mesmo com o app fechado**. Você confirma com um toque, o sistema dá a ele a menor mesa onde esse grupo cabe e abre o WhatsApp de confirmação já escrito, com o nome, o horário e a mesa.",
    "landing.s2.r2m": "Reservas",
    "landing.s2.r2n": "A mesma mesa não pode ser reservada duas vezes no mesmo horário. Nunca mais aquele vexame.",
    "landing.s2.r3t": "Ele escreve \"mila\", põe 3 e toca em Enviar.",
    "landing.s2.r3p": "A comanda sai pela impressora da cozinha; a grelha imprime só o que é dela. Se depois pedirem mais alguma coisa, **sai só o que é novo**, não a comanda inteira de novo.",
    "landing.s2.r3m": "Comandas",
    "landing.s2.r3n": "Do celular do garçom. Não precisa comprar tablets nem terminais.",
    "landing.s2.r4t": "Entra um Rappi e ele não suja o seu fechamento de caixa.",
    "landing.s2.r4p": "É lançado com o canal dele, o sistema calcula **a comissão e o que sobra líquido para você**, e deixa como recebimento futuro: esse dinheiro ainda não está na gaveta, então não entra no caixa de hoje.",
    "landing.s2.r4m": "Delivery",
    "landing.s2.r4n": "Painel \"os apps te devem\": quanto cada um tem que te depositar, sem abrir três painéis diferentes.",
    "landing.s2.r5t": "Você conta a gaveta e o caixa te diz sozinho se bate.",
    "landing.s2.r5p": "Compara **meio a meio**, não só o total: se falta dinheiro, ele te diz em qual. E dá para fechar às cegas, para que quem conta não veja quanto deveria ter.",
    "landing.s2.r5m": "Caixa",
    "landing.s2.r5n": "Quem fecha manda o relatório para o dono por WhatsApp num toque: total, mesas, ticket médio, despesas com nome e quanta gorjeta separar.",
    "landing.s2.r6t": "No dia seguinte você sabe quanto cada prato te deixa.",
    "landing.s2.r6p": "O estoque foi baixado sozinho com o que saiu para a cozinha ontem à noite. E em Custos você vê o número que importa: **quanto sobra para você em cada prato** e por quanto deveria estar cobrando.",
    "landing.s2.r6m": "Custos + Estoque",
    "landing.s2.r6n": "\"Atualizado há 2 meses\" ao lado de cada insumo: entrega os preços velhos que comem a sua margem.",

    "landing.s3.kicker": "O momento que mais quebra",
    "landing.s3.h2": "Entre o garçom anotar o pedido e a cozinha ler, se estraga metade das noites.",
    "landing.s3.lead": "Aqui não tem transcrição, não tem letra ilegível e não tem \"eu cantei o pedido\". **O garçom escreve três letras e o papel sai na cozinha.**",
    "landing.s3.key": "Enviar para a cozinha",
    "landing.s3.p1t": "Cada categoria sai pela impressora que você escolher",
    "landing.s3.p1d": "A cozinha imprime o que é dela e a grelha o que é dela. Você decide o que sai por onde, e dá para cortar o ticket por setor.",
    "landing.s3.p2t": "Se pedirem mais alguma coisa depois, sai só o que é novo",
    "landing.s3.p2d": "A comanda do acréscimo não imprime de novo o que a cozinha já está fazendo. Ninguém cozinha o mesmo prato duas vezes.",
    "landing.s3.p3t": "Se a impressora estiver desligada, o sistema não mente para você",
    "landing.s3.p3d": "Esses pratos não são dados como enviados. Ele te avisa antes de a cozinha não cozinhar, em vez de engolir o erro em silêncio.",
    "landing.s3.p4t": "Dois garçons na mesma mesa, sem se atropelar",
    "landing.s3.p4d": "Quem lança em segundo não apaga o que o primeiro lançou. E se cair a internet, a impressora continua imprimindo do mesmo jeito.",
    "landing.s3.cmMesa": "Mesa 4",
    "landing.s3.cmSub": "Comanda de cozinha · sáb 21:14",
    "landing.s3.cmMozo": "Garçom",
    "landing.s3.cmCub": "Pessoas",
    "landing.s3.cmNota1": "uma sem sal",
    "landing.s3.cmNota2": "sem pimenta",
    "landing.s3.cmEnviado": "Enviado",
    "landing.s3.cmImpresora": "Impressora",
    "landing.s3.cmStamp": "Impresso na cozinha",
    "landing.s3.cmFoot": "O garçom não escreveu nada à mão",

    "landing.s4.kicker": "Por que não dois sistemas",
    "landing.s4.h2": "Um prato. Um cadastro. Cinco lugares.",
    "landing.s4.lead": "Com um cardápio digital de um lado e um POS do outro, você cadastra cada prato duas vezes e os preços se dessincronizam sozinhos. **Aqui isso não pode acontecer: não existem dois lugares onde cadastrar.**",
    "landing.s4.origen": "Origem",
    "landing.s4.c1t": "Editor do cardápio",
    "landing.s4.c1d": "Nome, preço, foto, receita. Pelo celular, de pé no salão.",
    "landing.s4.c2t": "Cardápio QR",
    "landing.s4.c2d": "O que o cliente vê quando senta.",
    "landing.s4.c3t": "PDF impresso",
    "landing.s4.c3d": "O cardápio de papel, pronto para a gráfica.",
    "landing.s4.c4t": "Comandas",
    "landing.s4.c4d": "A tela do garçom e o ticket da cozinha.",
    "landing.s4.c5t": "Custos e estoque",
    "landing.s4.c5d": "Quanto sobra para você e o que ainda tem na geladeira.",
    "landing.s4.foot": "Se você já tem o seu cardápio com a gente, os seus pratos aparecem nas comandas sem que você cadastre um só. Nem mesmo os dos cardápios de cinco anos atrás.",
    "landing.s4.swipe": "Deslize para ver os cinco",

    "landing.s5.kicker": "O sistema completo",
    "landing.s5.h2": "Sete módulos. Uma conta só e um lugar só para olhar.",
    "landing.s5.thMod": "Módulo",
    "landing.s5.thQue": "O que resolve",
    "landing.s5.thPlan": "Plano",
    "landing.s5.planCarta": "Plano Cardápio",
    "landing.s5.planPack": "Pack Gestão",
    "landing.s5.m1n": "Cardápio digital",
    "landing.s5.m1d": "Cardápio sob medida para a sua marca com QR, em espanhol, inglês e português. Prato do dia, promoções, horários, avisos e categorias que aparecem sozinhas conforme a hora. Abre em menos de um segundo e o cliente não instala nada.",
    "landing.s5.m2n": "Reservas",
    "landing.s5.m2d": "Seu link próprio de reservas: o cliente escolhe a mesa tocando na planta do seu salão. Você comanda a noite pela tela \"Hoje\", com walk-ins, lista de espera e aviso por WhatsApp.",
    "landing.s5.m3n": "Custos e lucro",
    "landing.s5.m3d": "Você cadastra a receita e ele te diz quanto o prato deixa, com semáforo e com o preço pelo qual deveria estar cobrando. Vem com mais de 100 insumos argentinos já carregados com as suas unidades.",
    "landing.s5.m4n": "PDF do cardápio",
    "landing.s5.m4d": "O mesmo cardápio pronto para imprimir em papel, com editor manual da quebra de página para quando um prato fica partido no fim da folha.",
    "landing.s5.m5n": "Comandas / POS",
    "landing.s5.m5d": "Pedido por mesa pelo celular do garçom, comanda impressa por setor, pagamento com vários meios ao mesmo tempo, conta dividida, gorjetas por garçom e mesas que se juntam, mudam de lugar ou reabrem. Inclui os pedidos de Rappi e PedidosYa, com a comissão e o líquido calculados à parte.",
    "landing.s5.m6n": "Caixa / fechamento",
    "landing.s5.m6d": "Fechamento que compara meio a meio, fechamento às cegas, despesas e retiradas com nome e valor, e o relatório completo da noite pronto para mandar ao dono por WhatsApp.",
    "landing.s5.m7n": "Estoque e insumos",
    "landing.s5.m7d": "Montado sobre as receitas que você já cadastrou em Custos: o que se vende baixa o que se usa, e ele te avisa da falta no momento em que o garçom lança o pedido.",

    "landing.s6.kicker": "Quem já usa",
    "landing.s6.h2": "De Jujuy a Santa Cruz, abrindo hoje à noite.",
    "landing.s6.lead": "Churrascarias, sushi, hotéis boutique, foodtrucks e bares de bairro em 21 províncias e no Uruguai. Todos esses cardápios estão publicados e funcionando agora mesmo: **aqui não tem tela de mentira.**",
    "landing.s6.gTag": "Google · avaliações verificadas",
    "landing.s6.gNote": "Sem uma única avaliação fraca. Estão todas no perfil do Google do RestaurAdmin, com nome e sobrenome de quem escreveu.",
    "landing.s6.gLink": "Ler as avaliações",
    "landing.s6.more": "+190 mais",

    "landing.s7.kicker": "Vamos fazer as contas",
    "landing.s7.h2": "Não vamos te prometer que você vai vender 30% mais.",
    "landing.s7.lead": "Não temos como provar isso e você já ouviu essa frase mil vezes. A única coisa honesta que podemos fazer é esta conta: **quanto o seu restaurante fatura, e que parte disso é o sistema.**",
    "landing.s7.calcTitle": "O seu restaurante, em três números",
    "landing.s7.calcSub": "Coloque os seus. Nada fica salvo, não pedimos o seu e-mail e ninguém vai te ligar por causa disso.",
    "landing.s7.fMesas": "Mesas por noite",
    "landing.s7.fTicket": "Ticket médio",
    "landing.s7.fNoches": "Noites por semana",
    "landing.s7.dispLabel": "Faturamento mensal estimado",
    "landing.s7.dispSub": "Sobre {mesas} mesas por mês, com os seus próprios números.",
    "landing.s7.rowInstal": "Custo de instalação",
    "landing.s7.rowPerm": "Fidelidade mínima",
    "landing.s7.rowPermVal": "nenhuma",
    "landing.s7.punch1": "O sistema completo te custa o mesmo que uma mesa por mês.",
    "landing.s7.punchN": "O sistema completo te custa o mesmo que {n} mesas por mês.",
    "landing.s7.punchSub": "Se numa única noite o caixa bater onde hoje não bate, já se pagou.",
    "landing.s7.fine": "Estimativa com os seus próprios números: mesas × ticket × noites × 4,33 semanas.\nNão é promessa de vendas: é o que você já fatura.",

    "landing.s8.kicker": "Preços",
    "landing.s8.h2": "Dois planos. Sem letras miúdas e sem vendedor.",
    "landing.s8.lead": "Assinatura mensal, sem custo de instalação e sem fidelidade. Você pausa quando quiser e não perde nada: os dados ficam e, ao reativar, tudo volta como estava.",
    "landing.s8.mes": "/ mês",
    "landing.s8.p1k": "01 · Plano Cardápio",
    "landing.s8.p1t": "O cardápio, em ordem.",
    "landing.s8.p1who": "Para quem quer um cardápio digital sério, receber reservas e saber quanto cada prato deixa.",
    "landing.s8.p1note": "ARS · sem instalação · sem fidelidade",
    "landing.s8.p1f1": "Cardápio digital sob medida + QR",
    "landing.s8.p1f2": "Sistema de reservas completo",
    "landing.s8.p1f3": "Custos e lucro por prato",
    "landing.s8.p1f4": "PDF do cardápio para imprimir",
    "landing.s8.p1f5": "Comandas, caixa e estoque",
    "landing.s8.p1cta": "Começar pelo cardápio",
    "landing.s8.p2tag": "O restaurante inteiro",
    "landing.s8.p2k": "02 · Plano Completo",
    "landing.s8.p2t": "O restaurante, em ordem.",
    "landing.s8.p2who": "Tudo o que está acima mais o pack Gestão: o salão, a cozinha e o caixa dentro do mesmo sistema.",
    "landing.s8.p2note": "ARS · deixamos a impressora de comandas funcionando",
    "landing.s8.p2f1": "Todo o Plano Cardápio",
    "landing.s8.p2f2": "Comandas / POS por mesa",
    "landing.s8.p2f3": "Impressão de comandas e contas",
    "landing.s8.p2f4": "Caixa e fechamento com relatório por WhatsApp",
    "landing.s8.p2f5": "Pedidos dos apps de delivery",
    "landing.s8.p2f6": "Estoque e insumos",
    "landing.s8.p2cta": "Quero o sistema completo",

    "landing.s9.kicker": "Como você começa",
    "landing.s9.h2": "Você não mexe em nada até estar pronto.",
    "landing.s9.lead": "O trabalho de cadastrar o cardápio é nosso. Você revisa quando já estiver feito.",
    "landing.s9.step": "Passo",
    "landing.s9.s1t": "Você nos manda o seu cardápio",
    "landing.s9.s1d": "Do jeito que você tiver: um PDF, uma foto do papel, um Excel ou o link do que você já usa. A gente cadastra, prato por prato, com fotos e categorias.",
    "landing.s9.s1w": "Dia 1",
    "landing.s9.s2t": "Você revisa pelo seu celular",
    "landing.s9.s2d": "Mandamos o link para você ver como o seu cliente vai ver. Corrija o que quiser: a partir daí quem edita é você, sem pedir permissão para ninguém.",
    "landing.s9.s2w": "Dia 2",
    "landing.s9.s3t": "Ligamos o que você precisar",
    "landing.s9.s3d": "Se for pelo pack Gestão, deixamos a impressora de comandas imprimindo e os celulares dos garçons conectados. Presencial ou remoto, mas deixamos funcionando.",
    "landing.s9.s3w": "Nessa semana",

    "landing.s10.kicker": "As dúvidas de sempre",
    "landing.s10.h2": "O que te trava, respondido na lata.",
    "landing.s10.q1": "\"Emite nota fiscal?\"",
    "landing.s10.a1": "**Não, e preferimos te dizer isso antes e não depois.** A conta sai com a frase \"documento não válido como nota fiscal\" e o fechamento do dia é um relatório de controle interno. Para o fiscal você segue com o que já usa; isto organiza a sua operação, não substitui o seu emissor de notas.",
    "landing.s10.q2": "\"A internet cai no meio do serviço.\"",
    "landing.s10.a2": "Comandas continua funcionando na rede do restaurante: os garçons seguem lançando e a impressora segue imprimindo como se nada tivesse acontecido. Quando a conexão volta, sincroniza sozinho, sem ninguém ter que fazer nada.",
    "landing.s10.q3": "\"O computador do restaurante é uma relíquia.\"",
    "landing.s10.a3": "O agente de impressão roda até no Windows 7. É um arquivo só: dois cliques e fica instalado como serviço, ligando sozinho toda vez que você liga a máquina. Não precisa comprar nada novo.",
    "landing.s10.q4": "\"Meus garçons não vão saber usar.\"",
    "landing.s10.a4": "Eles escrevem as primeiras letras do prato, colocam a quantidade e apertam uma tecla. Quem sabe mandar um WhatsApp usa na primeira noite. E usam o próprio celular: não precisa comprar terminais.",
    "landing.s10.q5": "\"Já tenho o cardápio cadastrado com vocês.\"",
    "landing.s10.a5": "Então você tem 80% do trabalho feito. O pack Gestão é ativado na sua mesma conta e os seus pratos aparecem nas comandas sem recadastrar um só, mesmo que o seu cardápio seja de cinco anos atrás.",
    "landing.s10.q6": "\"E se eu começar e não me servir?\"",
    "landing.s10.a6": "Você pausa quando quiser e os dados ficam intactos. Ao reativar, tudo volta como estava. Não tem contrato de fidelidade, não tem custo de saída e não vamos botar ninguém para te ligar e te segurar.",

    "landing.cta.kicker": "O próximo passo",
    "landing.cta.h2a": "Conte sobre o seu restaurante e",
    "landing.cta.h2b": "a gente começa",
    "landing.cta.h2c": "esta semana.",
    "landing.cta.lead": "Você nos escreve e a gente diz qual plano serve e do que o seu restaurante precisa. Se topar, o cardápio nós cadastramos: você só revisa quando já está pronto.",
    "landing.cta.wa": "Fale com a gente pelo WhatsApp",
    "landing.cta.mail": "Mande um e-mail",
    "landing.cta.fine": "Sem fidelidade e sem vendedor te ligando.",
    "landing.cta.formTitle": "Ou deixe o seu telefone e a gente te liga",
    "landing.cta.formName": "Seu nome",
    "landing.cta.formBiz": "Nome do restaurante",
    "landing.cta.formTel": "Telefone",
    "landing.cta.formSend": "Quero que me liguem",
    "landing.cta.formSending": "Enviando…",
    "landing.cta.formOk": "Pronto. A gente te escreve hoje mesmo.",
    "landing.cta.formErr": "Não deu para enviar. Fale com a gente pelo WhatsApp que resolvemos.",
    "landing.rc.carta": "Cardápio digital + QR",
    "landing.rc.reservas": "Reservas",
    "landing.rc.costos": "Custos e lucro",
    "landing.rc.pdf": "PDF do cardápio",
    "landing.rc.pack": "Comandas + Caixa + Estoque",
    "landing.rc.incluida": "incluída",
    "landing.rc.incluidas": "incluídas",
    "landing.rc.incluidos": "incluídos",
    "landing.rc.incluido": "incluído",
    "landing.rc.packVal": "pack Gestão",
    "landing.rc.carga": "Cadastro do cardápio",
    "landing.rc.cargaVal": "a gente faz",
    "landing.rc.instal": "Instalação",
    "landing.rc.perm": "Fidelidade",
    "landing.rc.permVal": "nenhuma",
    "landing.rc.desde": "A partir de",

    "landing.foot.desc": "O sistema de gestão para restaurantes argentinos. Feito em Buenos Aires e sustentado por gente que atende o telefone.",
    "landing.foot.producto": "Produto",
    "landing.foot.modulos": "Os módulos",
    "landing.foot.cuenta": "Faça as contas",
    "landing.foot.precios": "Preços",
    "landing.foot.empezar": "Como você começa",
    "landing.foot.contacto": "Contato",
    "landing.foot.legal": "Jurídico",
    "landing.foot.priv": "Privacidade",
    "landing.foot.terms": "Termos",
    "landing.foot.data": "Exclusão de dados",
    "landing.foot.iva": "Preços em pesos argentinos · IVA incluído",
    "landing.dock.desde": "A partir de {precio}/mês",
    "landing.dock.sin": "sem fidelidade",
    "landing.dock.cta": "Vamos conversar",
    "landing.wa.msg": "Olá, quero ver o RestaurAdmin com o meu cardápio.",
  },
};

export default landing;
